import intl from "react-intl-universal";
import { TbKey } from "react-icons/tb";
import { isIssuerFundingPixLinkLabel } from "../../../../Services/baasVirtualShells";
import {
  formatPixKeyDate,
  formatPixKeyValue,
  getPixKeyStatusMeta,
  getPixKeyTypeMeta,
  shortPixAccountLabel,
} from "./constants";

const fundingLinkBadge = () => ({
  text: intl.get("pix.keys.fundingKeyBadge") || "CHAVE DE APORTE",
  title:
    intl.get("pix.keys.fundingKeyBadgeHint")
    || "Chave de aporte vinculada à reserva do programa. Créditos Pix financiam o emissor.",
});

const resolveLinkBadge = (item) => {
  if (!item?.linked || item.availableToLink) return null;
  const accountId = String(item.accountId || "").trim();
  const linkedAccountId = String(item.linkedAccountId || "").trim();
  const linkedAway = Boolean(
    linkedAccountId && accountId && linkedAccountId !== accountId,
  );
  if (linkedAway) {
    const dest = shortPixAccountLabel(item.linkedAccountLabel) || linkedAccountId;
    if (
      isIssuerFundingPixLinkLabel(item.linkedAccountLabel)
      || isIssuerFundingPixLinkLabel(dest)
    ) {
      return fundingLinkBadge();
    }
    const linkedLabel =
      intl.get("pix.keys.linkedAwayShort", { name: dest })
      || (dest ? `Vinculada · ${dest}` : null);
    return {
      text: linkedLabel || intl.get("pix.keys.linkedBadgeShort") || "Vinculada",
      title: linkedLabel || intl.get("pix.keys.linkedBadge") || "Vinculada à virtual",
    };
  }
  const origin = shortPixAccountLabel(item.originAccountLabel);
  if (
    isIssuerFundingPixLinkLabel(item.originAccountLabel)
    || isIssuerFundingPixLinkLabel(origin)
  ) {
    return fundingLinkBadge();
  }
  if (origin) {
    const linkedLabel =
      intl.get("pix.keys.linkedFromShort", { name: origin })
      || `Vinculada · ${origin}`;
    return { text: linkedLabel, title: linkedLabel };
  }
  return {
    text: intl.get("pix.keys.linkedBadgeShort") || "Vinculada",
    title: intl.get("pix.keys.linkedBadge") || "Vinculada",
  };
};

/**
 * Shared visual for a Pix key — used by Minhas chaves list and QR key dropdown.
 */
export const PixKeySummary = ({
  item,
  compact = false,
  showStatus = true,
  className = "",
  iconClassName = "h-11 w-11",
}) => {
  if (!item) return null;

  const typeMeta = getPixKeyTypeMeta(item.type);
  const statusMeta = showStatus ? getPixKeyStatusMeta(item.status) : null;
  const Icon = typeMeta?.icon || TbKey;
  const linkBadge = resolveLinkBadge(item);

  return (
    <div className={`flex min-w-0 items-center ${compact ? "gap-2" : "gap-3"} ${className}`}>
      <div
        className={`flex shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-brand-cream/60 ${
          compact ? "rounded-lg" : ""
        } ${iconClassName}`}
      >
        <Icon className={`${compact ? "h-4 w-4" : "h-5 w-5"} text-brand-dark`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-brand-dark">
            {typeMeta?.label || "Chave Pix"}
          </p>
          {item.isPrimary && (
            <span className="inline-flex rounded-full bg-[#e7a73f]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#a66f1d]">
              Principal
            </span>
          )}
          {linkBadge ? (
            <span
              className="inline-flex max-w-full truncate rounded-full bg-[#e7a73f]/15 px-2 py-0.5 text-[10px] font-bold tracking-wide text-[#a66f1d]"
              title={linkBadge.title || linkBadge.text}
            >
              {linkBadge.text}
            </span>
          ) : null}
          {statusMeta && (
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusMeta.className}`}
            >
              {statusMeta.label}
            </span>
          )}
        </div>
        <p className="truncate text-sm text-gray-600" title={item.value}>
          {formatPixKeyValue(item.type, item.value)}
        </p>
        {!compact && (
          <p className="text-[11px] text-gray-400">
            Cadastrada em {formatPixKeyDate(item.createdAt)}
          </p>
        )}
      </div>
    </div>
  );
};
